import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import {
  listShippingRates, updateShippingRate, getShippingRateAssignments, assignShippingRate, formatApiErrorDetail,
} from "@/admin/adminApi";
import { CATEGORY_CONFIGS } from "@/admin/categoryConfig";

const MONGO_CATEGORY_TO_SLUG = Object.fromEntries(
  Object.entries(CATEGORY_CONFIGS).map(([slug, cfg]) => [cfg.category, slug])
);
const categoryLabel = (category) => {
  if (category === "wellside") return "Wellside Canopies";
  const slug = MONGO_CATEGORY_TO_SLUG[category];
  return CATEGORY_CONFIGS[slug]?.label || category;
};
const editPath = (item) => {
  if (item.collection === "wellside") return `/admin/wellside-canopies/${item.slug}`;
  const slug = MONGO_CATEGORY_TO_SLUG[item.category];
  return slug ? `/admin/${slug}/${item.slug}` : null;
};

export default function AdminShippingRateEditPage() {
  const { id } = useParams();
  const [rate, setRate] = useState(null);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [isPOA, setIsPOA] = useState(false);
  const [items, setItems] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    listShippingRates().then((rates) => {
      const r = rates.find((x) => x.id === id);
      setRate(r);
      setName(r?.name || "");
      setPrice(r ? (r.priceCents / 100).toFixed(2) : "");
      setIsPOA(!!r?.isPOA);
    });
    getShippingRateAssignments(id).then((data) => {
      setItems(data);
      setChecked(new Set(data.filter((d) => d.assigned).map((d) => `${d.collection}:${d.id}`)));
    });
  }, [id]);

  const groups = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    const filtered = q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
    const byCategory = {};
    for (const item of filtered) {
      (byCategory[item.category] ||= []).push(item);
    }
    return Object.entries(byCategory)
      .map(([category, list]) => ({ category, label: categoryLabel(category), items: list }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, search]);

  const handleSaveRate = async (e) => {
    e.preventDefault();
    setError("");
    const priceCents = isPOA ? 0 : Math.round(parseFloat(price) * 100);
    if (!name.trim() || Number.isNaN(priceCents) || priceCents < 0) {
      setError("Enter a name and a valid price.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateShippingRate(id, { name: name.trim(), priceCents, isPOA });
      setRate(updated);
      setSuccessMsg("Saved.");
      setTimeout(() => setSuccessMsg(""), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = (item) => {
    const key = `${item.collection}:${item.id}`;
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleGroup = (group, selectAll) => {
    setChecked((prev) => {
      const next = new Set(prev);
      group.items.forEach((item) => {
        const key = `${item.collection}:${item.id}`;
        selectAll ? next.add(key) : next.delete(key);
      });
      return next;
    });
  };

  const handleSaveProducts = async () => {
    if (!items) return;
    setSavingProducts(true);
    setError("");
    try {
      const toAssign = items.filter((i) => checked.has(`${i.collection}:${i.id}`) && !i.assigned);
      const toUnassign = items.filter((i) => !checked.has(`${i.collection}:${i.id}`) && i.assigned);
      if (toAssign.length > 0) {
        await assignShippingRate(toAssign.map((i) => ({ collection: i.collection, id: i.id })), id);
      }
      if (toUnassign.length > 0) {
        await assignShippingRate(toUnassign.map((i) => ({ collection: i.collection, id: i.id })), null);
      }
      const data = await getShippingRateAssignments(id);
      setItems(data);
      listShippingRates().then((rates) => setRate(rates.find((x) => x.id === id)));
      setSuccessMsg(`Saved — ${toAssign.length} added, ${toUnassign.length} removed.`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSavingProducts(false);
    }
  };

  if (!rate || items === null) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  const dirty = items.some((i) => checked.has(`${i.collection}:${i.id}`) !== i.assigned);

  return (
    <div data-testid="admin-shipping-rate-edit-page">
      <Link to="/admin/shipping-rates" data-testid="admin-shipping-rate-back-link" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-[var(--brand-primary)] mb-6">
        <ArrowLeft size={14} /> All Shipping Rates
      </Link>

      <form onSubmit={handleSaveRate} className="mb-10 p-4 border border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Rate Name</label>
            <input
              data-testid="admin-shipping-rate-edit-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={rate.isDefault}
              className="w-full bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-sm text-white focus:border-[var(--brand-primary)] outline-none disabled:opacity-50"
            />
          </div>
          <div className="w-40">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Price (NZD)</label>
            <input
              data-testid="admin-shipping-rate-edit-price-input"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={isPOA}
              placeholder={isPOA ? "POA" : undefined}
              className="w-full bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-sm text-white focus:border-[var(--brand-primary)] outline-none disabled:opacity-40"
            />
          </div>
          <button type="submit" disabled={saving} data-testid="admin-shipping-rate-edit-save-button" className="btn-primary">
            {saving ? "Saving…" : "Save Rate"}
          </button>
        </div>
        <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
          <input
            type="checkbox"
            data-testid="admin-shipping-rate-edit-poa-checkbox"
            checked={isPOA}
            onChange={(e) => setIsPOA(e.target.checked)}
            className="accent-[var(--brand-primary)]"
          />
          <span className="text-white/70 text-xs font-mono">
            Mark as POA (Price on Application) — for big/heavy items like Bush Cabin or Tray Canopies.
            Checkout still lets customers pay for the item now; Delivery shows as "to be invoiced separately" instead of a flat rate.
          </span>
        </label>
      </form>
      {error && <p className="text-red-400 text-sm font-mono mb-6" data-testid="admin-shipping-rate-edit-error">{error}</p>}
      {successMsg && <p className="text-[var(--brand-primary)] text-sm font-mono mb-6" data-testid="admin-shipping-rate-edit-success">{successMsg}</p>}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-2xl text-white">Manage Products</h2>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-shipping-rate-product-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="bg-[var(--bg-main)] border border-[var(--border-default)] pl-9 pr-3 py-2 text-sm text-white focus:border-[var(--brand-primary)] outline-none w-64"
          />
        </div>
      </div>
      <p className="text-white/50 text-sm font-mono mb-4">
        Check the products that should ship at this rate. Unchecked products fall back to the Default rate — every product always carries an explicit rate, never "unassigned".
      </p>

      {groups.map((group) => {
        const allSelected = group.items.every((i) => checked.has(`${i.collection}:${i.id}`));
        return (
          <div key={group.category} className="mb-6" data-testid={`admin-shipping-rate-group-${group.category}`}>
            <div className="flex items-center justify-between border-b border-[var(--border-default)] pb-2 mb-2">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                {group.label} <span className="text-white/30">({group.items.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => toggleGroup(group, !allSelected)}
                data-testid={`admin-shipping-rate-select-all-${group.category}`}
                className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-[var(--brand-primary)]"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {group.items.map((item) => {
                const key = `${item.collection}:${item.id}`;
                const link = editPath(item);
                return (
                  <label
                    key={key}
                    data-testid={`admin-shipping-rate-product-${item.id}`}
                    className="flex items-center gap-3 border border-[var(--border-default)] bg-[var(--bg-secondary)] p-2 cursor-pointer hover:border-white/30"
                  >
                    <input
                      type="checkbox"
                      checked={checked.has(key)}
                      onChange={() => toggleItem(item)}
                      className="shrink-0 accent-[var(--brand-primary)]"
                    />
                    <img src={item.thumb} alt={item.name} className="w-10 h-8 object-cover shrink-0 border border-[var(--border-default)]" />
                    <span className="text-white text-xs truncate flex-1">{item.name}</span>
                    {link && (
                      <Link to={link} onClick={(e) => e.stopPropagation()} className="text-white/40 hover:text-[var(--brand-primary)] text-[10px] font-mono shrink-0">
                        Edit
                      </Link>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="sticky bottom-0 py-4 bg-[var(--bg-main)] border-t border-[var(--border-default)] mt-8">
        <button
          type="button"
          onClick={handleSaveProducts}
          disabled={!dirty || savingProducts}
          data-testid="admin-shipping-rate-save-products-button"
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {savingProducts ? "Saving…" : "Save Product Assignments"}
        </button>
      </div>
    </div>
  );
}
